import React from "react";

import {
  AddPhotoAlternateRounded,
  InsertDriveFileRounded,
  NoteAddRounded,
} from "@mui/icons-material";
import { Box } from "@mui/material";

import useModal from "../../../config/context/modalContext";
import { GreyPallet } from "../../../config/styles";
import { Dev } from "../../../config/util/consoleUtil";
import genCode from "../../../config/util/genCode";

import MainBorderButton from "../../MainBorderButton";

import { UserUploadedFileType } from "../../../types/database";
import MainText from "../../MainText";

type SimpleInputFileProps = {
  fileList: UserUploadedFileType[];
  setFileList: React.Dispatch<React.SetStateAction<UserUploadedFileType[]>>;
  onUpload?: (fileObject: UserUploadedFileType) => void;
  alt?: string;
  maxInput?: number;
  disabled?: boolean;
  isImage?: boolean;
  cover?: boolean;
  defaultValue?: string;
  clearDefaultValue?: () => void;
  imageHeight?: number;
};

export default function SimpleBase64({
  fileList,
  setFileList,
  onUpload,
  disabled,
  isImage,
  maxInput = 1,
  imageHeight,
  cover,
  defaultValue,
  clearDefaultValue,
}: SimpleInputFileProps) {
  const htmlIdCode = genCode();

  const { pushAlert } = useModal();

  const [isLoading, setIsLoading] = React.useState(false);

  const addFileToState = (fileObject: UserUploadedFileType) => {
    setFileList([...fileList, fileObject]);
  };

  const handleUploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setIsLoading(true);

      const rawFileBrowsed = event.target.files?.item(0);
      console.log(rawFileBrowsed);

      if (!rawFileBrowsed) return;

      const Reader = new FileReader();

      Reader.onload = async (event) => {
        const fileBase64 = event.target?.result as string;

        const fileObject = {
          ref: genCode(8),
          name: rawFileBrowsed.name,
          originalName: rawFileBrowsed.name,
          size: rawFileBrowsed.size,
          resource: fileBase64,
          resourceType: "base64",
        } as UserUploadedFileType;

        addFileToState(fileObject);

        if (onUpload) onUpload(fileObject);

        console.log(...new Dev("handleUploadFile", rawFileBrowsed.name, fileBase64).Success());
      };

      Reader.onerror = (err: any) => {
        throw new Error(err);
      };

      Reader.readAsDataURL(rawFileBrowsed);
    } catch (error: any) {
      console.log(...new Dev("handleUploadFile", error).Error());
      pushAlert({
        type: "error",
        message: error?.message || "Erro ao fazer upload do arquivo.",
      });
    } finally {
      setIsLoading(false);
      event.target.value = null as any;
    }
  };

  const ImageComponent = ({
    imageObject,
    onClick,
  }: {
    imageObject: string;
    onClick: () => void;
  }) => (
    <img
      onClick={onClick}
      src={imageObject}
      alt={"Imagem"}
      style={{
        cursor: "pointer",
        width: "100%",
        height: imageHeight || 60,
        objectFit: cover ? "cover" : "contain",
        borderRadius: 15,
        boxSizing: "border-box",
        border: `3px dashed ${GreyPallet.Regular}`,
      }}
    />
  );

  const FileComponent = ({
    fileObject,
    onClick,
  }: {
    fileObject: UserUploadedFileType;
    onClick: () => void;
  }) => (
    <div
      onClick={onClick}
      style={{
        cursor: "pointer",
        width: "100%",
        height: imageHeight || 60,
        objectFit: cover ? "cover" : "contain",
        borderRadius: 15,
        boxSizing: "border-box",
        border: `3px dashed ${GreyPallet.Regular}`,
        color: GreyPallet.Regular,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 5,
        padding: "0px 12px",
      }}
    >
      <InsertDriveFileRounded />
      <MainText color="regular" inline canOverflow>
        {fileObject.name}
      </MainText>
    </div>
  );

  return defaultValue ? (
    <ImageComponent
      imageObject={defaultValue}
      onClick={() => {
        if (clearDefaultValue) clearDefaultValue();
      }}
    />
  ) : (
    <Box>
      {fileList.map((fileObject) =>
        isImage ? (
          <ImageComponent
            key={fileObject.ref}
            imageObject={fileObject.resource}
            onClick={() => {
              setFileList(fileList.filter((item) => item.ref !== fileObject.ref));
            }}
          />
        ) : (
          <FileComponent
            key={fileObject.ref}
            fileObject={fileObject}
            onClick={() => {
              setFileList(fileList.filter((item) => item.ref !== fileObject.ref));
            }}
          />
        )
      )}

      {!!fileList.length && (
        <MainText size="small" color="regular">
          Clique no arquivo para remover
        </MainText>
      )}

      <Box width={"100%"} height={"100%"} position={"relative"}>
        <input
          disabled={isLoading || disabled}
          onChange={(e) => handleUploadFile(e)}
          type="file"
          accept={isImage ? "image/*" : undefined}
          id={"upload-file-" + htmlIdCode}
          style={{
            opacity: 0,
            zIndex: -1,
            position: "absolute",
          }}
        />

        <label
          htmlFor={"upload-file-" + htmlIdCode}
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            zIndex: 1000,
            top: 0,
            left: 0,
            cursor: "pointer",
          }}
        />

        {fileList.length < maxInput && (
          <Box
            mt={!!fileList.length ? 1 : 0}
            style={{
              width: "100%",
              height: "100%",
              position: "relative",
              zIndex: 100,
              top: 0,
              left: 0,
            }}
          >
            <MainBorderButton
              loading={isLoading}
              dashed
              text={`Inserir ${isImage ? "imagem" : "arquivo"}`}
              icon={isImage ? <AddPhotoAlternateRounded /> : <NoteAddRounded />}
              color="default"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
